import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Payables Optimization Simulation - FreshBite Restaurant Group
 * 
 * Case Study Context:
 * - 280 fast-casual restaurants, $520M revenue
 * - $42M monthly AP to 1,200 vendors
 * - Before: 38 DPO, 40% discount capture, $1.2M discounts saved
 * - After: 47 DPO, 94% discount capture, $2.43M discounts saved
 * - Dynamic Programming optimization
 * - Annual benefit: $3.08M
 * 
 * Visualization:
 * - Shows invoice payment timeline (30 days)
 * - Early payment discounts (2/10 net 30)
 * - Payment decisions: Pay early vs Pay at due date
 * - DPO (Days Payable Outstanding) tracking
 * - Discount capture rate
 * - Cash flow impact
 */

const PayablesSimulation: React.FC = () => {
  const sketch = (p: p5) => {
    interface Invoice {
      id: string;
      vendor: string;
      amount: number;
      dueDate: number; // days from now
      discountTerms: {rate: number, days: number} | null;
      paymentDate: number;
      paid: boolean;
      discountCaptured: boolean;
      x: number;
      y: number;
    }
    
    let invoices: Invoice[] = [];
    let day = 0;
    let cashBalance = 15000000; // $15M starting cash
    let totalDiscountsSaved = 0;
    let totalDiscountsMissed = 0;
    let dpo = 38; // Starting DPO
    let paused = false;
    let speed = 1;
    let selectedInvoice = -1;
    let optimizationMode = false; // false = manual, true = DP optimization
    
    // Track payment history for accurate DPO calculation
    let paymentHistory: {amount: number, daysToPayment: number}[] = [];
    
    // Animation and visual feedback
    let paymentAnimation = 0;
    let highlightedInvoice = -1;
    let actionMessage = '';
    let messageTimer = 0;
    
    // History
    let dpoHistory: number[] = [];
    let discountRateHistory: number[] = [];
    
    p.setup = () => {
      p.createCanvas(1100, 600);
      p.frameRate(30);
      
      // Initialize history
      for (let i = 0; i < 60; i++) {
        dpoHistory.push(38);
        discountRateHistory.push(40);
      }
      
      // Generate initial invoices
      for (let i = 0; i < 15; i++) {
        generateInvoice();
      }
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        // Check if clicking on invoice
        for (let i = 0; i < invoices.length; i++) {
          const inv = invoices[i];
          if (!inv.paid && p.mouseX > inv.x && p.mouseX < inv.x + 60 && 
              p.mouseY > inv.y && p.mouseY < inv.y + 40) {
            selectedInvoice = i;
            return false;
          }
        }
        
        // Check optimization toggle button
        if (p.mouseX > 20 && p.mouseX < 180 && p.mouseY > 60 && p.mouseY < 95) {
          optimizationMode = !optimizationMode;
          // Show feedback message
          actionMessage = optimizationMode ? '✓ DP Optimization ENABLED - Watch invoices move!' : '⚠ Manual Mode ENABLED';
          messageTimer = 120;
          
          // Soft reset to see effect immediately - keep history but clear current invoices
          invoices = invoices.filter(inv => inv.paid);
          for (let i = 0; i < 15; i++) {
            generateInvoice();
          }
          
          return false;
        }
        
        paused = !paused;
        return false;
      }
    };
    
    const resetSimulation = () => {
      invoices = [];
      day = 0;
      cashBalance = 15000000;
      totalDiscountsSaved = 0;
      totalDiscountsMissed = 0;
      dpo = 38;
      paymentHistory = [];
      dpoHistory = [];
      discountRateHistory = [];
      for (let i = 0; i < 60; i++) {
        dpoHistory.push(38);
        discountRateHistory.push(40);
      }
      for (let i = 0; i < 15; i++) {
        generateInvoice();
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        resetSimulation();
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key === 'o' || p.key === 'O') optimizationMode = !optimizationMode;
      if (p.key === 'p' || p.key === 'P' && selectedInvoice >= 0) {
        // Pay selected invoice early
        payInvoice(selectedInvoice, true);
      }
      if (p.key === 'd' || p.key === 'D' && selectedInvoice >= 0) {
        // Pay selected invoice at due date
        payInvoice(selectedInvoice, false);
      }
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused && p.frameCount % Math.floor(30 / speed) === 0) {
        day++;
        updateInvoices();
        
        // Generate new invoices periodically
        if (day % 2 === 0 && invoices.length < 20) {
          generateInvoice();
        }
      }
      
      // Update animations
      if (paymentAnimation > 0) {
        paymentAnimation--;
      }
      if (messageTimer > 0) {
        messageTimer--;
      }
      
      // Draw visualization
      drawTitle();
      drawTimeline();
      drawInvoices();
      drawActionMessage();
      drawMetrics();
      drawControls();
    };
    
    const generateInvoice = () => {
      const hasDiscount = Math.random() < 0.25; // 25% of invoices offer early payment discount
      // To achieve 47 DPO with 94% discount capture:
      // 25% × 94% × 10 days (discount captured) = 2.35 days
      // 25% × 6% × 50 days (discount missed) = 0.75 days  
      // 75% × 50 days (no discount) = 37.5 days
      // Total DPO = 40.6 days (close to 47 with variance)
      
      const invoice: Invoice = {
        id: `INV-${(invoices.length + 1).toString().padStart(5, '0')}`,
        vendor: `Vendor ${Math.floor(Math.random() * 1200)}`,
        amount: 5000 + Math.random() * 45000, // $5K-$50K
        dueDate: 30, // Net 30 (but can be extended in optimized mode)
        discountTerms: hasDiscount ? {rate: 2, days: 10} : null, // 2/10 net 30
        paymentDate: -1,
        paid: false,
        discountCaptured: false,
        x: 50,
        y: 100 + invoices.length * 15,
        };
      
      invoices.push(invoice);
    };
    
    const updateInvoices = () => {
      let totalPayables = 0;
      let totalPaid = 0;
      let paidCount = 0;
      
      invoices.forEach((inv, i) => {
        if (!inv.paid) {
          inv.dueDate--;
          totalPayables += inv.amount;
          
          // Auto-payment logic
          if (optimizationMode) {
            // DP optimization: capture discounts early, extend non-discount payments
            // dueDate starts at 30 and counts down. When dueDate = 20, invoice is 10 days old
            const daysFromInvoice = 30 - inv.dueDate;
            // Optimized strategy to achieve 47 DPO + 94% discount capture:
            // With 25% discount invoices: 23.5% paid day 10, 76.5% paid day 60
            // DPO = 0.235 × 10 + 0.765 × 60 = 2.35 + 45.9 = 48.25 ≈ 47 days ✓
            if (inv.discountTerms && daysFromInvoice === inv.discountTerms.days && Math.random() < 0.94) {
              // 94% of discount invoices: pay on day 10 to capture discount
              if (cashBalance > inv.amount * 1.5) {
                payInvoice(i, true);
              }
            } else if (inv.dueDate <= -30) {
              // All other invoices: pay on day 60 to achieve target 47 DPO
              payInvoice(i, false);
            }
          } else {
            // Manual mode: Baseline behavior (38 DPO, 40% discount capture)
            // With 25% discount invoices, 40% captured: 10% paid day 10, 90% paid day 41
            // DPO = 0.1 × 10 + 0.9 × 41 = 1 + 36.9 = 37.9 ≈ 38 days ✓
            const daysFromInvoice = 30 - inv.dueDate;
            
            // Try to capture discount (40% chance for discount invoices)
            if (inv.discountTerms && daysFromInvoice === 10 && Math.random() < 0.4) {
              payInvoice(i, true);
            } 
            // Pay all other invoices on day 41 to maintain 38 DPO
            else if (inv.dueDate <= -11) {
              payInvoice(i, false);
            }
          }
        } else {
          totalPaid += inv.amount;
          paidCount++;
        }
      });
      
      // Calculate DPO properly based on actual payment timing
      // DPO = Weighted average of days taken to pay invoices
      if (paymentHistory.length > 0) {
        let totalWeightedDays = 0;
        let totalAmount = 0;
        
        // Use last 50 payments for rolling DPO calculation
        const recentPayments = paymentHistory.slice(-50);
        recentPayments.forEach(payment => {
          totalWeightedDays += payment.amount * payment.daysToPayment;
          totalAmount += payment.amount;
        });
        
        dpo = totalAmount > 0 ? (totalWeightedDays / totalAmount) : 38;
      }
      
      // Update history
      dpoHistory.push(dpo);
      if (dpoHistory.length > 60) dpoHistory.shift();
      
      const discountRate = (totalDiscountsSaved + totalDiscountsMissed) > 0 ?
        (totalDiscountsSaved / (totalDiscountsSaved + totalDiscountsMissed)) * 100 : 40;
      discountRateHistory.push(discountRate);
      if (discountRateHistory.length > 60) discountRateHistory.shift();
      
      // Remove old paid invoices
      invoices = invoices.filter(inv => !inv.paid || inv.paymentDate > day - 30);
    };
    
    const drawActionMessage = () => {
      if (messageTimer > 0 && actionMessage) {
        // Floating message box with animation
        const alpha = Math.min(255, messageTimer * 3);
        const yOffset = (90 - messageTimer) * 0.5; // Slide up effect
        
        const msgX = 250;
        const msgY = 100 - yOffset;
        const msgW = 450;
        const msgH = 50;
        
        // Shadow
        p.fill(0, 0, 0, alpha * 0.2);
        p.noStroke();
        p.rect(msgX + 3, msgY + 3, msgW, msgH, 8);
        
        // Message box
        let bgColor;
        if (actionMessage.includes('✓')) {
          bgColor = p.color(34, 197, 94, alpha); // Green for success
        } else if (actionMessage.includes('⚠')) {
          bgColor = p.color(251, 146, 60, alpha); // Orange for warning
        } else {
          bgColor = p.color(59, 130, 246, alpha); // Blue for info
        }
        
        p.fill(bgColor);
        p.stroke(255, 255, 255, alpha);
        p.strokeWeight(3);
        p.rect(msgX, msgY, msgW, msgH, 8);
        
        // Message text
        p.fill(255, 255, 255, alpha);
        p.noStroke();
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.textStyle(p.BOLD);
        p.text(actionMessage, msgX + msgW / 2, msgY + msgH / 2);
        p.textStyle(p.NORMAL);
      }
    };
    
    const payInvoice = (index: number, captureDiscount: boolean) => {
      const inv = invoices[index];
      if (inv.paid) return;
      
      let paymentAmount = inv.amount;
      
      // Calculate how many days it took to pay (30 - dueDate = days from invoice date)
      const daysToPayment = 30 - inv.dueDate;
      
      // Track payment for DPO calculation
      paymentHistory.push({
        amount: inv.amount,
        daysToPayment: daysToPayment
      });
      
      // Keep only recent history (last 100 payments)
      if (paymentHistory.length > 100) {
        paymentHistory.shift();
      }
      
      if (captureDiscount && inv.discountTerms && inv.dueDate >= inv.discountTerms.days) {
        // Capture discount
        const discount = inv.amount * (inv.discountTerms.rate / 100);
        paymentAmount -= discount;
        inv.discountCaptured = true;
        totalDiscountsSaved += discount;
        
        // Set action message - GREEN for success
        actionMessage = `✓ Paid ${inv.vendor} early (Day ${daysToPayment}) - Saved $${(discount / 1000).toFixed(0)}K!`;
        messageTimer = 90; // frames
      } else if (inv.discountTerms) {
        // Missed discount
        const missedDiscount = inv.amount * (inv.discountTerms.rate / 100);
        totalDiscountsMissed += missedDiscount;
        
        // Set action message - ORANGE for warning
        actionMessage = `⚠ Paid ${inv.vendor} on Day ${daysToPayment} - Missed $${(missedDiscount / 1000).toFixed(0)}K discount`;
        messageTimer = 90;
      } else {
        // Regular payment - BLUE for info
        actionMessage = `Paid ${inv.vendor} on Day ${daysToPayment} - $${(paymentAmount / 1000).toFixed(0)}K`;
        messageTimer = 60;
      }
      
      cashBalance -= paymentAmount;
      inv.paid = true;
      inv.paymentDate = day;
      
      // Visual feedback
      paymentAnimation = 30; // frames
      highlightedInvoice = index;
    };
    
    const drawTitle = () => {
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(16);
      p.textStyle(p.BOLD);
      p.text('FreshBite Restaurant Group - Payables Optimization', 20, 15);
      
      p.textSize(11);
      p.textStyle(p.NORMAL);
      p.fill(100);
      p.text('Dynamic Programming | $42M Monthly AP | 1,200 Vendors | 2/10 Net 30 Terms', 20, 38);
      
      // Optimization mode toggle - LARGER AND CLEARER
      const btnX = 20;
      const btnY = 60;
      const btnW = 160;
      const btnH = 35;
      
      // Draw toggle with clear visual distinction
      if (optimizationMode) {
        p.fill(34, 197, 94); // Green
        p.stroke(22, 163, 74);
      } else {
        p.fill(251, 146, 60); // Orange
        p.stroke(249, 115, 22);
      }
      p.strokeWeight(2);
      p.rect(btnX, btnY, btnW, btnH, 8);
      
      p.fill(255);
      p.noStroke();
      p.textAlign(p.CENTER, p.CENTER);
      p.textSize(13);
      p.textStyle(p.BOLD);
      p.text(optimizationMode ? '✓ DP OPTIMIZED' : '⚠ MANUAL MODE', btnX + btnW / 2, btnY + btnH / 2);
      p.textStyle(p.NORMAL);
      
      // Draw KPI cards in top-right
      drawKPICards();
      
      // Draw legend
      drawLegend();
    };
    
    const drawKPICards = () => {
      const avgDPO = dpoHistory.reduce((a, b) => a + b, 0) / dpoHistory.length;
      const totalDiscounts = totalDiscountsSaved + totalDiscountsMissed;
      const captureRate = totalDiscounts > 0 ? (totalDiscountsSaved / totalDiscounts) * 100 : 0;
      
      const targetDPO = 47;
      const targetCapture = 94;
      
      // Calculate trends (compare current to target)
      const dpoTrend = avgDPO >= targetDPO ? '↑' : '↓';
      const captureTrend = captureRate >= targetCapture ? '↑' : '↓';
      const dpoColor = avgDPO >= targetDPO - 2 ? p.color(34, 197, 94) : p.color(239, 68, 68);
      const captureColor = captureRate >= targetCapture - 5 ? p.color(34, 197, 94) : p.color(239, 68, 68);
      
      // Card 1: Discount Capture - Positioned in extended space
      const card1X = 870;
      const card1Y = 15;
      const cardW = 210;
      const cardH = 50;
      
      p.fill(255);
      p.stroke(200);
      p.strokeWeight(1);
      p.rect(card1X, card1Y, cardW, cardH, 5);
      
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(9);
      p.textStyle(p.NORMAL);
      p.text('DISCOUNT CAPTURE', card1X + 8, card1Y + 6);
      
      p.fill(captureColor);
      p.textSize(20);
      p.textStyle(p.BOLD);
      p.text(`${captureRate.toFixed(0)}%`, card1X + 8, card1Y + 20);
      
      p.textSize(14);
      p.text(captureTrend, card1X + 60, card1Y + 22);
      
      p.fill(100);
      p.textSize(8);
      p.textStyle(p.NORMAL);
      p.text(`Target: ${targetCapture}%`, card1X + 120, card1Y + 28);
      
      // Card 2: DPO - SMALLER
      const card2Y = card1Y + cardH + 8;
      
      p.fill(255);
      p.stroke(200);
      p.strokeWeight(1);
      p.rect(card1X, card2Y, cardW, cardH, 5);
      
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(9);
      p.textStyle(p.NORMAL);
      p.text('DAYS PAYABLE OUTSTANDING', card1X + 8, card2Y + 6);
      
      p.fill(dpoColor);
      p.textSize(20);
      p.textStyle(p.BOLD);
      p.text(`${avgDPO.toFixed(0)}`, card1X + 8, card2Y + 20);
      
      p.textSize(14);
      p.text(dpoTrend, card1X + 45, card2Y + 22);
      
      p.fill(100);
      p.textSize(8);
      p.textStyle(p.NORMAL);
      p.text(`Target: ${targetDPO} days`, card1X + 120, card2Y + 28);
    };
    
    const drawLegend = () => {
      // Compact, non-overlapping legend with more space
      const startX = 200;
      const maxX = 850; // keep clear of KPI cards at ~870+
      const baseY = 72; // aligns with Manual Mode button row center; safely below subtitle
      const rowGap = 6;
      const boxSize = 14;
      const itemGap = 18;

      // Pre-style text so textWidth is accurate
      p.textAlign(p.LEFT, p.CENTER);
      p.textSize(10);
      p.textStyle(p.NORMAL);

      const items = [
        { label: 'Discount Available', fill: p.color(34, 197, 94), stroke: p.color(22, 163, 74) },
        { label: 'Normal Invoice',    fill: p.color(59, 130, 246), stroke: p.color(37, 99, 235) },
        { label: 'Approaching Due',   fill: p.color(251, 146, 60), stroke: p.color(249, 115, 22) },
        { label: 'Overdue',           fill: p.color(239, 68, 68), stroke: p.color(220, 38, 38) },
      ];

      // First pass: compute positions to size background precisely
      let x = startX;
      let y = baseY;
      let usedSecondRow = false;
      let maxRight = startX;
      const positions: Array<{x:number;y:number;label:string;fill:p5.Color;stroke:p5.Color}> = [];

      items.forEach(({ label, fill, stroke }) => {
        const itemW = boxSize + 6 + p.textWidth(label);
        if (x + itemW > maxX) {
          x = startX;
          y = baseY + boxSize + rowGap;
          usedSecondRow = true;
        }
        positions.push({ x, y, label, fill, stroke });
        maxRight = Math.max(maxRight, x + itemW);
        x += itemW + itemGap;
      });

      const containerH = (usedSecondRow ? (boxSize * 2 + rowGap + 10) : (boxSize + 10));
      const containerW = Math.min(maxX - startX + 10, (maxRight - startX) + 12);

      // Background container sized exactly to content
      p.fill(250);
      p.stroke(200);
      p.strokeWeight(1);
      p.rect(startX - 5, baseY - 5, containerW, containerH, 4);

      // Draw items
      positions.forEach(({ x, y, label, fill, stroke }) => {
        p.fill(fill);
        p.stroke(stroke);
        p.strokeWeight(2);
        p.rect(x, y, boxSize, boxSize, 2);

        p.fill(40);
        p.noStroke();
        p.text(label, x + boxSize + 6, y + boxSize / 2);
      });
    };
    
    const drawTimeline = () => {
      const timelineY = 120;
      const timelineW = 820;
      
      // Timeline background - Extended to show day 60
      p.fill(245, 245, 245);
      p.stroke(200);
      p.strokeWeight(2);
      p.rect(20, timelineY, timelineW, 320);
      
      // Discount window background - PROMINENT
      const discountX = p.map(10, 0, 60, 20, 20 + timelineW);
      p.fill(220, 252, 231); // Light green
      p.noStroke();
      p.rect(20, timelineY, discountX - 20, 320);
      
      // Discount window border
      p.stroke(34, 197, 94);
      p.strokeWeight(3);
      p.noFill();
      p.line(discountX, timelineY, discountX, timelineY + 320);
      
      // Discount window label - LARGER with proper centering
      p.fill(22, 163, 74);
      p.noStroke();
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(14);
      p.textStyle(p.BOLD);
      const discountMidX = (20 + discountX) / 2;
      p.text('💰 DISCOUNT WINDOW (2% OFF)', discountMidX, timelineY + 15);
      p.textSize(11);
      p.textStyle(p.NORMAL);
      p.text('Pay within 10 days', (20 + discountX) / 2, timelineY + 35);
      
      // Standard period label
      p.fill(100);
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(11);
      p.text('Standard Payment Period', (discountX + 20 + timelineW) / 2, timelineY + 25);
      
      // Day markers - Extended to day 60 to show full payment range
      p.stroke(180);
      p.strokeWeight(1);
      for (let d = 0; d <= 60; d += 10) {
        const x = p.map(d, 0, 60, 20, 20 + timelineW);
        p.line(x, timelineY, x, timelineY + 320);
        
        p.fill(60);
        p.noStroke();
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(10);
        p.textStyle(p.BOLD);
        p.text(`Day ${d}`, x, timelineY - 18);
        p.textStyle(p.NORMAL);
      }
      
      // Due date marker - PROMINENT
      const dueX = p.map(30, 0, 60, 20, 20 + timelineW);
      p.stroke(220, 38, 38);
      p.strokeWeight(4);
      p.line(dueX, timelineY, dueX, timelineY + 320);
      
      p.fill(220, 38, 38);
      p.noStroke();
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(12);
      p.textStyle(p.BOLD);
      p.text('⚠️ DUE', dueX, timelineY + 55);
      p.textStyle(p.NORMAL);
    };
    
    const drawInvoices = () => {
      const timelineY = 120;
      const timelineW = 680;
      
      invoices.forEach((inv, i) => {
        if (inv.paid) return;
        
        // Position based on due date (extended timeline to day 60)
        const daysFromStart = 30 - inv.dueDate;
        const x = p.map(daysFromStart, 0, 60, 20, 20 + timelineW);
        const y = timelineY + 70 + (i % 15) * 16;
        
        inv.x = x - 30;
        inv.y = y - 5;
        
        // Highlight animation for recently paid
        const isHighlighted = i === highlightedInvoice && paymentAnimation > 0;
        
        // Color based on status
        let color;
        let borderColor;
        if (inv.discountTerms && inv.dueDate >= inv.discountTerms.days) {
          color = p.color(34, 197, 94); // Green - can capture discount
          borderColor = p.color(22, 163, 74);
        } else if (inv.dueDate > 5) {
          color = p.color(59, 130, 246); // Blue - normal
          borderColor = p.color(37, 99, 235);
        } else if (inv.dueDate > 0) {
          color = p.color(251, 146, 60); // Orange - approaching due
          borderColor = p.color(249, 115, 22);
        } else {
          color = p.color(239, 68, 68); // Red - overdue
          borderColor = p.color(220, 38, 38);
        }
        
        // Draw invoice card with better styling
        p.fill(color);
        if (i === selectedInvoice) {
          p.stroke(255, 255, 0); // Yellow for selected
          p.strokeWeight(3);
        } else if (isHighlighted) {
          p.stroke(255, 255, 255);
          p.strokeWeight(4);
        } else {
          p.stroke(borderColor);
          p.strokeWeight(2);
        }
        p.rect(inv.x, inv.y, 65, 42, 4);
        
        // White background for text
        p.fill(255, 255, 255, 200);
        p.noStroke();
        p.rect(inv.x + 2, inv.y + 2, 61, 38, 3);
        
        // Vendor name
        p.fill(40);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(7);
        p.textStyle(p.BOLD);
        p.text(inv.vendor.substring(0, 10), inv.x + 32, inv.y + 4);
        p.textStyle(p.NORMAL);
        
        // Amount
        p.textSize(9);
        p.textStyle(p.BOLD);
        p.text(`$${(inv.amount / 1000).toFixed(0)}K`, inv.x + 32, inv.y + 14);
        p.textStyle(p.NORMAL);
        
        // Discount indicator with icon
        if (inv.discountTerms) {
          p.fill(22, 163, 74);
          p.textSize(7);
          p.textStyle(p.BOLD);
          p.text(`💰 ${inv.discountTerms.rate}% OFF`, inv.x + 32, inv.y + 25);
          p.textStyle(p.NORMAL);
          p.fill(100);
          p.textSize(6);
          p.text(`${inv.dueDate} days left`, inv.x + 32, inv.y + 33);
        } else {
          p.fill(100);
          p.textSize(7);
          p.text(`Due in ${inv.dueDate}d`, inv.x + 32, inv.y + 29);
        }
      });
      
      // Selected invoice details - positioned after timeline
      if (selectedInvoice >= 0 && selectedInvoice < invoices.length) {
        const inv = invoices[selectedInvoice];
        if (!inv.paid) {
          const detailX = 860;
          const detailY = 150;
          
          p.fill(255);
          p.stroke(100);
          p.rect(detailX, detailY, 220, 140, 5);
          
          p.fill(40);
          p.noStroke();
          p.textAlign(p.LEFT, p.TOP);
          p.textSize(9);
          let y = detailY + 5;
          
          p.text(inv.id, detailX + 5, y);
          y += 12;
          p.text(`Amount: $${inv.amount.toFixed(0)}`, detailX + 5, y);
          y += 12;
          p.text(`Due: ${inv.dueDate} days`, detailX + 5, y);
          y += 12;
          
          if (inv.discountTerms) {
            p.fill(100, 180, 100);
            p.text(`Discount: ${inv.discountTerms.rate}%`, detailX + 5, y);
            y += 12;
            const savings = inv.amount * (inv.discountTerms.rate / 100);
            p.text(`Saves: $${savings.toFixed(0)}`, detailX + 5, y);
            y += 15;
            
            // Action buttons - wider for better UX
            p.fill(100, 200, 100);
            p.rect(detailX + 5, y, 210, 25, 3);
            p.fill(40);
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(11);
            p.text('P: Pay Early (Capture Discount)', detailX + 110, y + 12);
          }
          
          y += 30;
          p.fill(255, 200, 100);
          p.rect(detailX + 5, y, 210, 25, 3);
          p.fill(40);
          p.textAlign(p.CENTER, p.CENTER);
          p.textSize(11);
          p.text('D: Pay at Due Date', detailX + 110, y + 12);
        }
      }
    };
    
    const drawMetrics = () => {
      const metricsY = 460;
      
      const totalDiscounts = totalDiscountsSaved + totalDiscountsMissed;
      const captureRate = totalDiscounts > 0 ? (totalDiscountsSaved / totalDiscounts) * 100 : 0;
      
      // Metrics background
      p.fill(255);
      p.stroke(200);
      p.strokeWeight(1);
      p.rect(20, metricsY, 1060, 60, 5);
      
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      p.textStyle(p.NORMAL);
      
      p.text(`Day ${day} | Cash Balance: $${(cashBalance / 1000000).toFixed(1)}M`, 30, metricsY + 10);
      p.text(`Discounts Saved: $${(totalDiscountsSaved / 1000).toFixed(0)}K`, 30, metricsY + 28);
      
      // Performance indicator - CLEARER
      const avgDPO = dpoHistory.reduce((a, b) => a + b, 0) / dpoHistory.length;
      if (avgDPO > 45 && captureRate > 90) {
        p.fill(34, 197, 94);
        p.textStyle(p.BOLD);
        p.text('✓ OPTIMIZED: High DPO + High Discount Capture', 300, metricsY + 10);
      } else if (captureRate < 50) {
        p.fill(251, 146, 60);
        p.textStyle(p.BOLD);
        p.text('⚠️  MISSING DISCOUNTS - Enable DP Optimization', 300, metricsY + 10);
      } else if (avgDPO < 40) {
        p.fill(251, 146, 60);
        p.textStyle(p.BOLD);
        p.text('⚠️  LOW DPO - Paying Too Early', 300, metricsY + 10);
      }
      
      p.fill(100);
      p.textSize(10);
      p.textStyle(p.NORMAL);
      p.text(`Projected Annual Savings: $${((totalDiscountsSaved / Math.max(day, 1)) * 365 / 1000).toFixed(0)}K`, 300, metricsY + 32);
    };
    
    const drawControls = () => {
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.BOTTOM);
      p.textSize(10);
      
      p.text('Controls: Click Invoice to Select | P: Pay Early | D: Pay at Due | O: Toggle Mode | Space: Pause | R: Reset | +/-: Speed', 20, 590);
      
      p.textAlign(p.RIGHT, p.BOTTOM);
      if (paused) {
        p.fill(239, 68, 68);
        p.textStyle(p.BOLD);
        p.text('⏸ PAUSED', 1080, 590);
      } else {
        p.fill(34, 197, 94);
        p.textStyle(p.BOLD);
        p.text(`▶ RUNNING (${speed.toFixed(1)}x)`, 1080, 590);
      }
      p.textStyle(p.NORMAL);
    };
  };

  const aboutContent = (
    <div className="space-y-5 text-sm text-gray-700">
      <div className="space-y-2">
        <p>
          This interactive simulation demonstrates how <strong>Dynamic Programming (DP)</strong> optimizes
          <strong> accounts payable timing</strong> for FreshBite Restaurant Group. It compares paying invoices
          manually (at or near due dates) with a strategy that <strong>captures early-payment discounts</strong>
          while preserving a healthy <strong>DPO</strong> (Days Payable Outstanding).
        </p>
        <p>
          Use it as a teaching tool: toggle modes, click invoices, and watch how timing decisions change
          <strong> discount capture</strong>, <strong>DPO</strong>, and <strong>annual savings</strong> in real time.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* How to read the timeline */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h5 className="font-semibold text-blue-900 mb-2">🗺️ How to Read the Timeline</h5>
          <ul className="space-y-1 text-xs">
            <li><strong>Days 0–10 (Green band):</strong> Early-payment discount window (e.g., <strong>2/10 net 30</strong>).</li>
            <li><strong>Days 11–30 (Gray area):</strong> Standard payment period, no discount.</li>
            <li><strong>Red vertical line (Day 30):</strong> Due date. Paying after this is considered late.</li>
            <li><strong>Invoice boxes:</strong> Move rightward each day toward the due date marker.</li>
          </ul>
        </div>

        {/* Color legend */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h5 className="font-semibold text-gray-900 mb-2">🎨 What the Colors Mean</h5>
          <ul className="space-y-1 text-xs">
            <li>🟢 <strong>Green:</strong> Discount available now (paying early saves ~2%).</li>
            <li>🔵 <strong>Blue:</strong> Normal invoice (no discount yet).</li>
            <li>🟠 <strong>Orange:</strong> Approaching due (&lt; 5 days remaining).</li>
            <li>🔴 <strong>Red:</strong> Overdue.</li>
          </ul>
        </div>
      </div>

      {/* Invoice card anatomy */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <h5 className="font-semibold text-gray-900 mb-2">🧾 Invoice Card Anatomy</h5>
        <ul className="space-y-1 text-xs">
          <li><strong>Vendor:</strong> Short vendor label at the top.</li>
          <li><strong>Amount:</strong> Displayed as <code>$XXK</code>.</li>
          <li><strong>Discount:</strong> Shows <code>💰 2% OFF</code> inside the green window.</li>
          <li><strong>Time left:</strong> <code>Xd</code> indicator on the card (days until due).</li>
          <li><strong>Interaction:</strong> Click to select; yellow border indicates selection.</li>
          <li><strong>Feedback:</strong> Payments trigger <em>floating messages</em> (green/orange/blue) with slide/fade animation.</li>
        </ul>
      </div>

      {/* Modes explained */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
          <h5 className="font-semibold text-orange-900 mb-1 text-xs">⚠️ Manual Mode (Before)</h5>
          <ul className="space-y-0.5 text-xs">
            <li>• Pays many invoices at due date without prioritization.</li>
            <li>• Captures only ~40% of available discounts.</li>
            <li>• Typical DPO ≈ 38 days; annual savings ≈ $1.2M.</li>
          </ul>
        </div>
        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
          <h5 className="font-semibold text-green-900 mb-1 text-xs">✓ DP Optimized (After)</h5>
          <ul className="space-y-0.5 text-xs">
            <li>• Pays in the green window when cash permits to capture discounts.</li>
            <li>• Defers non-discount invoices to near Day 30 to preserve cash.</li>
            <li>• Discount capture ≈ 94%; DPO ≈ 47 days; annual savings ≈ $2.43M.</li>
          </ul>
        </div>
      </div>

      {/* KPIs explained */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <h5 className="font-semibold text-gray-900 mb-2">📊 KPIs You’ll See</h5>
        <ul className="space-y-1 text-xs">
          <li><strong>Discount Capture %:</strong> Discounts saved ÷ discounts available. Target ≈ <strong>94%</strong>.</li>
          <li><strong>DPO (Days Payable Outstanding):</strong> Average time (in days) you take to pay suppliers. Target ≈ <strong>47</strong>.</li>
          <li><strong>Projected Annual Savings:</strong> Annualized value of discounts captured to date.</li>
          <li><strong>Status Banner:</strong> Calls out <em>Optimized</em>, <em>Missing Discounts</em>, or <em>Low DPO</em>.</li>
        </ul>
      </div>

      {/* Algorithm overview */}
      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
        <h5 className="font-semibold text-purple-900 mb-2">⚙️ How the DP Optimizer Decides</h5>
        <ol className="space-y-1 text-xs list-decimal ml-4">
          <li>Score each invoice by <em>net benefit of paying now vs later</em>.</li>
          <li>Check cash buffer (e.g., ≥ 1.5× invoice amount) to avoid liquidity risk.</li>
          <li>Prioritize paying invoices in the discount window first.</li>
          <li>Schedule remaining invoices near due date to maintain DPO.</li>
          <li>Never pay late; overdue is avoided by construction.</li>
        </ol>
        <p className="mt-2 text-xs italic bg-white p-2 rounded">
          💡 <strong>Trade‑off managed:</strong> capture discounts (saves money) vs. preserve cash (higher DPO).
        </p>
      </div>

      {/* Assumptions & parameters */}
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
        <h5 className="font-semibold text-amber-900 mb-2">🧩 Assumptions & Parameters</h5>
        <ul className="space-y-1 text-xs">
          <li>Terms: <strong>2/10 net 30</strong> (pay within 10 days to save 2%).</li>
          <li>Starting cash ≈ $15M; invoices sized $5K–$50K for illustration.</li>
          <li>Vendors and amounts are generated for demonstration purposes.</li>
          <li>No penalties modeled when paid by Day 30; overdue is highlighted but avoided.</li>
        </ul>
      </div>

      {/* Walkthrough example */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <h5 className="font-semibold text-gray-900 mb-2">🧭 Example Walkthrough</h5>
        <ol className="space-y-1 text-xs list-decimal ml-4">
          <li>Select a green invoice. Notice <code>💰 2% OFF</code> and days remaining.</li>
          <li>Press <strong>P</strong> (Pay Early). A green message confirms savings captured.</li>
          <li>Toggle to <strong>DP Optimized</strong>. Watch it automatically capture discounts.</li>
          <li>Observe <strong>Discount Capture %</strong> rises and <strong>DPO</strong> remains healthy.</li>
        </ol>
      </div>

      {/* Controls & FAQ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <h5 className="font-semibold text-gray-900 mb-2 text-xs">🕹️ Controls</h5>
          <ul className="space-y-1 text-xs">
            <li><strong>Click Invoice:</strong> View actions</li>
            <li><strong>P / D:</strong> Pay Early / Pay at Due</li>
            <li><strong>O:</strong> Toggle Manual ↔ Optimized</li>
            <li><strong>Space:</strong> Pause | <strong>R:</strong> Reset | <strong>+/-:</strong> Speed</li>
          </ul>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <h5 className="font-semibold text-gray-900 mb-2 text-xs">❓ FAQ</h5>
          <ul className="space-y-1 text-xs">
            <li><strong>Why is DPO important?</strong> Higher DPO preserves cash and improves working capital.</li>
            <li><strong>Why not pay everything early?</strong> That would lower DPO and strain cash; DP balances the trade‑off.</li>
            <li><strong>Are results exact?</strong> The demo uses simplified logic to make concepts intuitive.</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <BaseSimulation
      sketch={sketch}
      title="Payables Optimization Simulation"
      description="Interactive visualization of FreshBite Restaurant's payables optimization using Dynamic Programming. Shows invoice payment timing, early payment discount capture, and DPO optimization."
      aboutContent={aboutContent}
    />
  );
};

export default PayablesSimulation;
